import type { BigNumberOccurrence } from '../../../models/occurrence';
import { getStepLabelId } from '../trackingUtils';
import type { StageSummary } from '../types';
import StageHeat from './StageHeat';
import useMtText from './useMtText';
import './TrackingSummary.scss';

interface ITrackingSummaryProps {
    bigNumbers: BigNumberOccurrence;
    lateCount: number;
    finished: boolean;
    firstLoad: boolean;
    refreshing: boolean;
    stages: StageSummary[];
    showStages: boolean;
    activeStage: string | null;
    onStageClick: (step: string) => void;
}

const TrackingSummary = ({
    bigNumbers,
    lateCount,
    finished,
    firstLoad,
    refreshing,
    stages,
    showStages,
    activeStage,
    onStageClick,
}: ITrackingSummaryProps) => {
    const { t, msg, formatNumber, formatDuration } = useMtText();

    const kpis = [
        {
            key: 'main',
            value: formatNumber(
                finished ? bigNumbers.totalOfFinalizedOccurrence : bigNumbers.totalOfInProgressOccurrence
            ),
            label: finished
                ? msg('intl-msg:kanban.bigNumbers.completedTickets')
                : msg('intl-msg:kanban.bigNumbers.inProgressTickets'),
        },
        { key: 'late', value: formatNumber(lateCount), label: msg('intl-msg:kanban.filterRow.inAlert'), bad: true },
        {
            key: 'notStarted',
            value: formatNumber(Math.max(0, bigNumbers.totalOfNotStartedOccurrence)),
            label: msg('intl-msg:kanban.bigNumbers.notStartedTickets'),
        },
        {
            key: 'vehicles',
            value: formatNumber(bigNumbers.totalChassis ?? 0),
            label: msg('intl-msg:kanban.bigNumbers.vehicles'),
        },
        {
            key: 'average',
            value: formatDuration(bigNumbers.averageInService * 1000),
            label: msg('intl-msg:kanban.bigNumbers.averageTimePerVehicle'),
        },
    ];

    return (
        <div
            className={`mt-panel mt-summary${showStages ? '' : ' mt-summary--nostages'}`}
            data-refreshing={refreshing && !firstLoad}
            aria-busy={refreshing}
        >
            <div className='mt-kpis'>
                {kpis.map(kpi => (
                    <div key={kpi.key} className='mt-kpi'>
                        {firstLoad ? (
                            <span className='mt-skel' style={{ width: 56, height: 24 }} />
                        ) : (
                            <b className={`mt-num${kpi.bad && lateCount > 0 ? ' mt-kpi--bad' : ''}`}>{kpi.value}</b>
                        )}
                        <span>{kpi.label}</span>
                    </div>
                ))}
            </div>

            {showStages && (
                <div className='mt-stages' aria-label={t('summary.stagesHint')}>
                    {stages.map(stage => (
                        <button
                            type='button'
                            key={stage.stepKey}
                            className='mt-stage'
                            aria-pressed={activeStage === stage.step}
                            title={
                                stage.estimateMs
                                    ? t('summary.estimated', { time: formatDuration(stage.estimateMs) })
                                    : undefined
                            }
                            onClick={() => onStageClick(stage.step)}
                        >
                            <span className='mt-stage__name'>{msg(getStepLabelId(stage.step))}</span>
                            <span className='mt-stage__time mt-num'>
                                {firstLoad ? (
                                    <span className='mt-skel' style={{ width: 48, height: 14 }} />
                                ) : (
                                    formatDuration(stage.averageMs)
                                )}
                            </span>
                            <span className='mt-stage__count mt-num'>
                                {t('summary.vehicles', { count: formatNumber(stage.count) })}
                            </span>
                            <StageHeat stage={stage} />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default TrackingSummary;
